/**
 * RecordManualPaymentDialog — guided modal for admins to record a payment.
 *
 * Step 1: Choose full or partial payment
 * Step 2: Fill in payment details (method, reference, date)
 * Success: Confirmation with auto-close
 */

import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  CheckCircle2,
  Banknote,
  CircleDollarSign,
  ArrowLeft,
  Landmark,
  Wallet,
  CreditCard,
  ArrowRightLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useRecordManualPayment } from "../api/salesOrderHooks";

const MODE_OPTIONS = [
  { value: "Bank Transfer", label: "โอนเงิน", Icon: Landmark },
  { value: "Cash", label: "เงินสด", Icon: Wallet },
  { value: "Credit Card", label: "บัตรเครดิต", Icon: CreditCard },
  { value: "Wire Transfer", label: "โอนผ่านธนาคาร", Icon: ArrowRightLeft },
] as const;

interface RecordManualPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  salesOrderName: string;
  grandTotal: number;
  currency: string;
}

type PaymentType = "full" | "partial" | null;
type Step = "choose" | "details" | "success";

function formatMoney(n: number, currency: string) {
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n) + ` ${currency}`;
}

export function RecordManualPaymentDialog({
  open,
  onOpenChange,
  salesOrderName,
  grandTotal,
  currency,
}: RecordManualPaymentDialogProps) {
  const [step, setStep] = useState<Step>("choose");
  const [paymentType, setPaymentType] = useState<PaymentType>(null);
  const [amount, setAmount] = useState("");
  const [modeOfPayment, setModeOfPayment] = useState("Bank Transfer");
  const [referenceNo, setReferenceNo] = useState("");
  const [referenceDate, setReferenceDate] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const recordMutation = useRecordManualPayment(salesOrderName);
  const autoCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const parsedAmount = paymentType === "full" ? grandTotal : (parseFloat(amount) || 0);
  const isAmountValid = parsedAmount > 0 && parsedAmount <= grandTotal;

  useEffect(() => {
    if (!open) {
      setStep("choose");
      setPaymentType(null);
      setAmount("");
      setModeOfPayment("Bank Transfer");
      setReferenceNo("");
      setReferenceDate(new Date().toISOString().slice(0, 10));
      if (autoCloseTimer.current) {
        clearTimeout(autoCloseTimer.current);
        autoCloseTimer.current = null;
      }
    }
  }, [open, grandTotal]);

  const handleChoose = (type: PaymentType) => {
    setPaymentType(type);
    if (type === "full") setAmount(String(grandTotal));
    else setAmount("");
    setStep("details");
  };

  const handleBack = () => {
    setStep("choose");
    setPaymentType(null);
  };

  const handleConfirm = () => {
    recordMutation.mutate(
      {
        mode_of_payment: modeOfPayment,
        reference_no: referenceNo || undefined,
        reference_date: referenceDate || undefined,
        ...(paymentType === "partial" ? { amount: parsedAmount } : {}),
      },
      {
        onSuccess: () => {
          setStep("success");
          autoCloseTimer.current = setTimeout(() => {
            onOpenChange(false);
          }, 1800);
        },
        onError: (error) => {
          toast.error(
            error.message || "ไม่สามารถบันทึกการชำระเงินได้ กรุณาลองอีกครั้ง",
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden">

        {/* ── Step: Success ── */}
        {step === "success" && (
          <div className="flex flex-col items-center gap-4 py-12 px-6">
            <div className="rounded-full bg-green-50 p-4">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
            <div className="text-center space-y-1">
              <p className="text-base font-semibold">บันทึกการชำระเงินเรียบร้อย</p>
              <p className="text-sm text-muted-foreground">
                {formatMoney(parsedAmount, currency)}
              </p>
            </div>
          </div>
        )}

        {/* ── Step 1: Choose full or partial ── */}
        {step === "choose" && (
          <div className="px-6 py-6 space-y-5">
            {/* Header */}
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">บันทึกการชำระเงิน</h2>
              <p className="text-sm text-muted-foreground">
                ยอดค้างชำระ {formatMoney(grandTotal, currency)}
              </p>
            </div>

            {/* Two cards */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleChoose("full")}
                className={cn(
                  "flex flex-col items-center gap-3 rounded-2xl border-2 p-5 transition-all duration-150",
                  "hover:border-sky-400 hover:bg-sky-50/50 dark:hover:bg-sky-950/20",
                  "focus:outline-none focus:ring-2 focus:ring-sky-400/40",
                  "border-border bg-card",
                )}
              >
                <div className="rounded-full bg-sky-50 dark:bg-sky-950/30 p-3">
                  <Banknote className="h-6 w-6 text-sky-600" />
                </div>
                <div className="text-center space-y-0.5">
                  <p className="text-sm font-semibold">ชำระเต็มจำนวน</p>
                  <p className="text-xs text-muted-foreground">Full Payment</p>
                </div>
                <p className="text-base font-semibold text-sky-600">
                  {formatMoney(grandTotal, currency)}
                </p>
              </button>

              <button
                onClick={() => handleChoose("partial")}
                className={cn(
                  "flex flex-col items-center gap-3 rounded-2xl border-2 p-5 transition-all duration-150",
                  "hover:border-amber-400 hover:bg-amber-50/50 dark:hover:bg-amber-950/20",
                  "focus:outline-none focus:ring-2 focus:ring-amber-400/40",
                  "border-border bg-card",
                )}
              >
                <div className="rounded-full bg-amber-50 dark:bg-amber-950/30 p-3">
                  <CircleDollarSign className="h-6 w-6 text-amber-600" />
                </div>
                <div className="text-center space-y-0.5">
                  <p className="text-sm font-semibold">ชำระบางส่วน</p>
                  <p className="text-xs text-muted-foreground">Partial Payment</p>
                </div>
                <p className="text-xs text-muted-foreground">ระบุจำนวนเอง</p>
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Payment details ── */}
        {step === "details" && (
          <div className="space-y-0">
            {/* Header bar */}
            <div className="flex items-center gap-3 px-6 pt-5 pb-4">
              <button
                onClick={handleBack}
                className="rounded-full p-1.5 hover:bg-muted transition-colors"
              >
                <ArrowLeft className="h-4 w-4 text-muted-foreground" />
              </button>
              <div className="flex-1">
                <h2 className="text-base font-semibold">
                  {paymentType === "full" ? "ชำระเต็มจำนวน" : "ชำระบางส่วน"}
                </h2>
              </div>
              {paymentType === "full" && (
                <span className="rounded-full bg-sky-50 dark:bg-sky-950/30 px-3 py-1 text-xs font-semibold text-sky-600">
                  {formatMoney(grandTotal, currency)}
                </span>
              )}
            </div>

            {/* Form */}
            <div className="space-y-4 px-6 pb-6">
              {/* Partial: amount input */}
              {paymentType === "partial" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">จำนวนเงิน</label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={grandTotal}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="0.00"
                      className="text-lg font-semibold pr-14"
                      autoFocus
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                      {currency}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    ยอดรวมทั้งหมด {formatMoney(grandTotal, currency)}
                  </p>
                </div>
              )}

              {/* Payment method — card buttons */}
              <div className="space-y-2">
                <label className="text-sm font-medium">วิธีชำระเงิน</label>
                <div className="grid grid-cols-2 gap-2">
                  {MODE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setModeOfPayment(opt.value)}
                      className={cn(
                        "flex items-center gap-2 rounded-xl border px-3 py-2.5 text-sm transition-all duration-150",
                        modeOfPayment === opt.value
                          ? "border-sky-400 bg-sky-50 dark:bg-sky-950/30 font-medium text-sky-700 dark:text-sky-300"
                          : "border-border hover:border-muted-foreground/40 hover:bg-muted/50",
                      )}
                    >
                      <opt.Icon className={cn("h-4 w-4", modeOfPayment === opt.value ? "text-sky-600" : "text-muted-foreground")} />
                      <span>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Reference number */}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  เลขอ้างอิง
                  <span className="text-muted-foreground font-normal ml-1">(ไม่บังคับ)</span>
                </label>
                <Input
                  placeholder="เลขที่โอน, เลขใบเสร็จ"
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                />
              </div>

              {/* Payment date */}
              <div className="space-y-2">
                <label className="text-sm font-medium">วันที่ชำระ</label>
                <Input
                  type="date"
                  value={referenceDate}
                  onChange={(e) => setReferenceDate(e.target.value)}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl"
                  onClick={() => onOpenChange(false)}
                  disabled={recordMutation.isPending}
                >
                  ยกเลิก
                </Button>
                <Button
                  className="flex-1 rounded-xl"
                  onClick={handleConfirm}
                  disabled={recordMutation.isPending || !isAmountValid}
                >
                  {recordMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      กำลังบันทึก...
                    </>
                  ) : (
                    <>บันทึกการชำระ {isAmountValid && `• ${formatMoney(parsedAmount, currency)}`}</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
